import { gql } from "lib/apolloClient";
import { studentsAlertFragment } from "modules/CommonFragments";
import {
  classMaterialsFragment,
  classMaterialOutlineFragment,
  classMaterialsFragmentWithoutDepth,
  attachmentFragment,
  assessmentFragment,
  linkedResourcesFragment,
  singleCourseV2Fragment,
} from "./ClassMaterialsFragments";
import {
  studentDetailsFragment,
  assignedStudentsFragment,
} from "ClassRoom/modules/ClassRoomFragments";

export const getClassMaterialsFeedQuery = gql`
  query getOrganizationResourcesFeedV2(
    $parentId: ID!
    $parentType: ENTITY_TYPE_ENUM!
    $orderBy: ORDER_BY_ENUM
    $filters: OrganizationResourceFilter
    $after: String
    $studentIds: [ID!]
    $first: Int
    $skipAssignedStudents: Boolean!
  ) {
    node(id: $parentId, type: $parentType) {
      id
      ... on Course {
        id
        organizationResourcesFlatFeed(
          first: $first
          orderBy: $orderBy
          filters: $filters
          after: $after
          orderByDirection: ASC
        ) {
          edges {
            node {
              ...classMaterialsWithChildrenItem
              isNew
            }
          }
          pageInfo {
            hasNextPage
            hasPreviousPage
            startCursor
            endCursor
            __typename
          }
          resourceTypeBasedCount {
            totalCount
            __typename
          }
        }
        __typename
      }
      __typename
    }
  }
  ${classMaterialsFragment.classMaterialsWithChildren}
`;

export const getClassMaterialsOutlineFeedQuery = gql`
  query getOrganizationResourcesFeedV2(
    $parentId: ID!
    $parentType: ENTITY_TYPE_ENUM!
    $filters: OrganizationResourceFilter
    $orderBy: ORDER_BY_ENUM
  ) {
    node(id: $parentId, type: $parentType) {
      id
      ... on Course {
        id
        organizationResourcesFlatFeedOutline(filters: $filters) {
          id
          label
          depth
          state
          ...classMaterialsWithChildrenItem
        }
        __typename
      }
      __typename
    }
  }
  ${classMaterialOutlineFragment.classMaterialsWithChildren}
`;

export const getSyncStatusQuery = gql`
  query getSyncStatus($id: ID!, $type: [JOB_TYPE_ENUM!], $courseIds: [ID!]) {
    node(id: $id, type: ORGANIZATION) {
      id
      ... on Organization {
        syncStatus(type: $type, courseIds: $courseIds) {
          id
          status
        }
      }
    }
  }
`;

export const getOrganizationResourceMultiNodeQuery = gql`
  query getOrganizatonResources(
    $ids: [ID!]
    $orderBy: ORDER_BY_ENUM
    $filters: OrganizationResourceFilter
    $studentIds: [ID!]
    $skipAssignedStudents: Boolean!
  ) {
    multiNode(ids: $ids, type: ORGANIZATION_RESOURCE) {
      ... on OrganizationResource {
        ...classMaterialsWithChildrenItem
      }
    }
  }
  ${classMaterialsFragmentWithoutDepth.classMaterialsWithChildren}
`;

export const getClassMaterialsFeedForImportQuery = gql`
  query getClassMaterialsFeedForImport(
    $parentId: ID!
    $parentType: ENTITY_TYPE_ENUM!
    $orderBy: ORDER_BY_ENUM
    $filters: OrganizationResourceFilter
    $after: String
    $first: Int
  ) {
    node(id: $parentId, type: $parentType) {
      id
      ... on Course {
        id
        organizationResourcesFlatFeed(
          first: $first
          orderBy: $orderBy
          filters: $filters
          after: $after
          orderByDirection: ASC
        ) {
          edges {
            node {
              id
              label
              resourceType
              depth
              item {
                ... on Assignment {
                  id
                  deadline
                  contentType
                  publishedAt
                  state {
                    state
                    publishedAt
                    updatedAt
                  }
                  closeSubmissionDate
                  isStudentSubmissionEnabled
                  visibility
                  content {
                    id
                    ... on AssignmentResource {
                      assignmentTitle: title
                    }
                    ... on Assessment {
                      ...assessmentItem
                    }
                  }
                }
                ... on OrganizationResource {
                  id
                  attachment {
                    ...classMaterialsAttachmentItem
                  }
                }
              }
              childrenHierarchyInfo(filters: $filters) {
                maxDepth
                totalCount
                totalCountWithFolder
              }
              attachment {
                ...classMaterialsAttachmentItem
              }
              parent: parentResource {
                id
              }
            }
          }
          pageInfo {
            hasNextPage
            hasPreviousPage
            startCursor
            endCursor
          }
          resourceTypeBasedCount {
            totalCount
          }
        }
      }
    }
  }
  ${attachmentFragment}
  ${assessmentFragment}
`;

export const getCompleteResourcesFeed = gql`
  query getCompleteResources(
    $parentId: ID!
    $parentType: ENTITY_TYPE_ENUM!
    $orderBy: ORDER_BY_ENUM
    $filters: OrganizationResourceFilter
  ) {
    node(id: $parentId, type: $parentType) {
      id
      ... on Course {
        id
        organizationResourcesFlatFeed(
          first: 1000
          orderBy: $orderBy
          filters: $filters
        ) {
          edges {
            node {
              id
            }
          }
        }
      }
    }
  }
`;

// if you update something here, also update the backward
// compatibilty functions in graphql helpers file
export const getEntireClassFlowFeedQuery = gql`
  query getEntireClassFlowFeed(
    $parentId: ID!
    $filters: OrganizationResourceFilter
  ) {
    node(id: $parentId, type: COURSE) {
      id
      ... on Course {
        id
        organizationResourcesFlatFeed(
          orderBy: DISPLAY_SEQUENCE
          filters: $filters
          orderByDirection: ASC
          first: 10000
        ) {
          edges {
            node {
              id
              label
              depth
              resourceType
              parent: parentResource {
                id
              }
              item {
                id
                ... on Assignment {
                  id
                  content {
                    ... on Assessment {
                      id
                      type
                      academicTerm {
                        id
                        label
                        lockingStatus
                      }
                      assessmentType {
                        id
                        value
                      }
                    }
                  }
                }
              }
              childrenHierarchyInfo(filters: $filters) {
                maxDepth
                totalCount
                totalCountWithFolder
              }
              childrenV2(
                orderBy: DISPLAY_SEQUENCE
                filters: $filters
                first: 1000
              ) {
                edges {
                  node {
                    id
                  }
                }
              }
            }
          }
          pageInfo {
            hasNextPage
            hasPreviousPage
            startCursor
            endCursor
            __typename
          }
          resourceTypeBasedCount {
            totalCount
            __typename
          }
        }
        __typename
      }
    }
  }
`;

export const getClassFlowStudentsQuery = gql`
  query getOrganizatonResources(
    $ids: [ID!]
    $userArchivedFilters: UserArchivedFilters
    $studentIds: [ID!]
    $unreadMessageCountFilters: UnreadMessageCountFilters
  ) {
    multiNode(ids: $ids, type: ORGANIZATION_RESOURCE) {
      ... on OrganizationResource {
        id
        item {
          ... on Assignment {
            id
            ...assignedStudents
          }
        }
        students {
          totalCount
          edges {
            node {
              ...studentItem
              ... on Student {
                id
                alertTypes {
                  ...alertTypeFields
                }
              }
            }
          }
        }
      }
    }
  }
  ${studentDetailsFragment.basicStudentDetails}
  ${assignedStudentsFragment.assignedStudents}
  ${studentsAlertFragment}
`;

export const getLinkedResources = gql`
  query getOrganizatonResources(
    $ids: [ID!]
    $userArchivedFilters: UserArchivedFilters
  ) {
    multiNode(ids: $ids, type: ORGANIZATION_RESOURCE) {
      ... on OrganizationResource {
        id
        ...linkedResources
        students {
          totalCount
          edges {
            node {
              id
            }
          }
        }
      }
    }
  }
  ${linkedResourcesFragment}
`;

export const getUserCoursesV2Query = gql`
  query getUserCoursesV2(
    $userId: ID!
    $first: Int
    $after: String
    $courseFilters: CourseFilter
    $courseGradeFilters: CourseGradeFilters
  ) {
    node(id: $userId, type: STAFF) {
      id
      ... on Staff {
        coursesV2(first: $first, after: $after, filters: $courseFilters) {
          totalCount
          pageInfo {
            hasNextPage
            endCursor
          }
          edges {
            node {
              ... on Course {
                ...courseV2
              }
            }
          }
        }
      }
    }
  }
  ${singleCourseV2Fragment.singleCourseV2}
`;
